import type { Page } from "@sveltejs/kit";
import { Observable, Subject, map, mergeScan, of, startWith, switchMap } from "rxjs";
import { writable } from "svelte/store";


/**
 * Useful to check if the current route page reacts to location search parameters. This
 * is a companion function when using `selectAddress` whos first parameter needs to know
 * if a navigation to `/` needs to happen or whether the current page will change content
 * based on the location search params.
 * @param routeId The page's route id to check.
 * @returns `true` if the route is location aware.
 */
export function isLocationAwareRoute(routeId: string) {
	return [
		'/(app)/delivery/[vertical=vertical]/[name]/[id]'
	].indexOf(routeId) > -1
}

export interface SideNavState {
	items: SideNavMainItem[];
}

export function getSideNavContext() {

	const subjMainItems = new Subject<{ items: SideNavMainItem[] }>();
	const subjSubItems = new Subject<{ mainItemId: string | number, items: SideNavItem[] }>();

	return {

		/**
		 * Sets the main navigation items of the side nav.
		 * @param items The navigation items to render on the side nav.
		 */
		setMainItems(items: SideNavMainItem[] = []) {
			subjMainItems.next({ items })
		},

		/**
		 * Add sub items to a main navigation item of the side nav.
		 * @param mainItemId The identifier of the main navigation items to add the sub items to.
		 * @param items The navigation sub items to render under the main item.
		 */
		setActiveSubItems(mainItemId: string | number, items: SideNavItem[]) {
			subjSubItems.next({ mainItemId, items })
		},

		createSideNavItemsStream(page$: Observable<Page>) {
			return subjMainItems.pipe(
				switchMap(main => subjSubItems.pipe(
					mergeScan((acc, update) => {
						const item = acc.items.find(item => item.id === update.mainItemId);
						if (item) item.items = update.items;
						return of(acc);
					}, main),
					startWith(main),
				)),
				switchMap(main => page$.pipe(
					map(p => {
						main.items.forEach(item => {
							if (item.active) item.isActive = item.active(p, item);
							if (item.setHref) item.href = item.setHref(p, item);
							if (item.setScroll) {
								item.scroll = item.setScroll(p);
							} else {
								item.scroll = false;
							}
							// set sub items
							if (item.items) {
								item.items.forEach(sub => {
									if (sub.active) sub.isActive = sub.active(p, sub);
									if (sub.setHref) sub.href = sub.setHref(p, sub);
								});
							}
						});
						return { ...main };
					}),
				)),
				// shareReplay(1), // don't replay, we want to re-render on every page change
			);
		}

	}
}

export interface SideNavItem {
	/**
	 * An identifier used as reference when setting sub-items.
	 */
	id: string | number,

	/**
	 * The link text to display to the user.
	 */
	title: string,

	/**
	 * Optional icon to display next to the link text.
	 */
	icon?: string,

	/**
	 * The link destination url.
	 */
	href?: string,

	/**
	 * A method to set the item target href.
	 * @param currentPage The current Page store value.
	 * @param item The item being tested.
	 * @returns A valid href string.
		*/
	setHref?: (currentPage: Page, item: SideNavMainItem) => string,

	/**
	 * Used by the template.
	 */
	isActive?: boolean,

	/**
	 * A method to test the active state of an item.
	 * @param currentPage The current Page store value.
	 * @param item The item being tested.
	 * @returns `true` if the item is active.
		*/
	active?: (currentPage: Page, item: SideNavMainItem) => boolean,

	/**
	 * If true the default scroll reset (scroll to top) will occur on goto
	 */
	scroll?: boolean;
	setScroll?: (currentPage: Page) => boolean,
}

export interface SideNavMainItem extends SideNavItem {
	/**
	 * Optional sub items to the main item.
	 */
	items?: SideNavItem[];
}

/**
 * Sorts URL search params and places them in a string. Useful for comparing URLs.
 * @param url Any URL with (or without) search params.
 * @returns A string with sorted search params.
 */
export function organiseUrlSearch(url: URL) {
	//get array of search param keys
	const keys = Array.from(url.searchParams.keys());
	// sort keys alphabetically and case-insensitive
	keys.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
	// return comparison string
	return keys.map(key => `${key}=${url.searchParams.get(key)}`).join('&');
}

/*
* Writeable store to manage address modal states between stores and regular address input
* When you click on 'change store' in the sidenav, it should only bring up the stores in the address modal
* When you click on the address button in the top nav, it should only bring up the addresses in the address modal
*/
export const showStoreSelectModal = writable(false);

export const showSignInModal = writable(false);


